import * as React from "react";
import { cx } from "../util.js";

export interface TabItem {
  /** Stable id for the tab. */
  id: string;
  /** Visible label. */
  label: React.ReactNode;
  /** ID of the associated panel, rendered by the caller. */
  panelId?: string;
}

export interface TabsProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** Tabs to render. */
  items: TabItem[];
  /** Currently active tab id. */
  value: string;
  /** Called with the new tab id when a tab is selected. */
  onChange: (id: string) => void;
  /** Visual style: underline (default) or pill segmented control. */
  appearance?: "underline" | "segmented";
  orientation?: "horizontal" | "vertical";
}
/** Controlled tab strip. Used by the codex entry editor and detail panels. */
export function Tabs({ items, value, onChange, appearance = "underline", orientation = "horizontal", className, onKeyDown, ...rest }: TabsProps) {
  const tabRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const id = React.useId();
  const selected = Math.max(0, items.findIndex(item => item.id === value));
  return (
    <div
      role="tablist"
      className={cx(appearance === "segmented" ? "ip-segmented" : "ip-tabs", className)}
      {...rest}
      aria-orientation={orientation}
      onKeyDown={event => {
        onKeyDown?.(event);
        if (event.defaultPrevented) return;
        const index = tabRefs.current.findIndex(tab => tab === event.target);
        if (index < 0 || !items.length) return;
        const rtl = getComputedStyle(event.currentTarget).direction === "rtl";
        const nextKey = orientation === "vertical" ? "ArrowDown" : rtl ? "ArrowLeft" : "ArrowRight";
        const prevKey = orientation === "vertical" ? "ArrowUp" : rtl ? "ArrowRight" : "ArrowLeft";
        let next: number;
        if (event.key === "Home") next = 0;
        else if (event.key === "End") next = items.length - 1;
        else if (event.key === nextKey) next = (index + 1) % items.length;
        else if (event.key === prevKey) next = (index + items.length - 1) % items.length;
        else return;
        event.preventDefault();
        tabRefs.current[next]?.focus();
        onChange(items[next].id);
      }}
    >
      {items.map((t, index) => {
        const active = t.id === value;
        return (
          <button
            key={t.id}
            id={`${id}-${t.id}`}
            ref={element => { tabRefs.current[index] = element; }}
            tabIndex={index === selected ? 0 : -1}
            aria-controls={t.panelId}
            role="tab"
            type="button"
            aria-selected={active}
            className="ip-tab"
            data-active={active ? "true" : undefined}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}
